import { BillingRates } from "@mcleod/ar/src/BillingRates";
import { OrderRates } from "./OrderRates";
import { CommonDialogs } from "@mcleod/common";
import {
    BlurEvent, DataSource, DataSourceMode, DropdownSelectionEvent, LookupModelSelectionEvent, Table, TableRow,
    TableRowMode, Textbox
} from "@mcleod/components";
import { Api, Currency, CurrencySettings, ModelRow } from "@mcleod/core";
import { EdiBillingRates } from "@mcleod/datafusion/src/EdiBillingRates";
import { RecOrderRates } from "./RecOrderRates";

interface TaxData {
    taxRow: ModelRow;
    taxTextbox?: Textbox;
    masterTableId?: string;
    table?: Table;
    onLookupSelection?: boolean;
    rowType?: "ORDER" | "QUOTE" | "BILLING" | "EDI_BILLING" | "REC_ORDER";
}


type RatesLayout = BillingRates | EdiBillingRates | OrderRates | RecOrderRates;

export class TaxUtil {
    private ratesLayout: RatesLayout;
    private ordersDatasource: DataSource;
    private _currencyType: string;

    public initialize(ratesLayout: RatesLayout) {
        this.ratesLayout = ratesLayout;
        this.ordersDatasource = ratesLayout.mainDataSource;
    }

    async textboxExciseTaxIdOnLookupModelSelection(event: LookupModelSelectionEvent, masterTableId?: string) {
        if (event.hasSelection() === true) {
            const textboxDescr: Textbox = TableRow.getContainingTableRow(event.target)?.findComponentById("textboxDescr") as Textbox;
            if (textboxDescr != null)
                textboxDescr.text = event.selection.get("descr");
            await this.exciseIdChanged(event.target, masterTableId);
        }
    }

    async exciseIdChanged(textbox: Textbox, masterTableId: string) {
        const taxRow = textbox.boundRow;
        await this.calculateTaxCharges({
            taxRow,
            taxTextbox: textbox,
            masterTableId,
            onLookupSelection: true
        }, true);
    }

    async calculateTaxCharges(props: TaxData, newSelection: boolean = false, fromCreateButton: boolean = false) {
        if (props.taxTextbox?.validateSimple() === false) {
            return;
        }

        if (this.ordersDatasource == null || this.ordersDatasource.activeRow == null) {
            return;
        }
        const orderInfo = this.ordersDatasource.getDataboundValues(null, true);
        orderInfo.set("id", this.ordersDatasource.activeRow.get("id"));
        const response = await Api.search("lme/dispatch/calculate-tax", {
            "tax_row": props.taxRow,
            "order_info": orderInfo,
            "order_type": this.getOrderType(),
            "new_selection": newSelection,
            "master_table_id": props.masterTableId
        })
        await this.processTaxFromApi(response, props, fromCreateButton);
        this.tableTaxOnContentsChanged();
    }

    async processTaxFromApi(response: any, props: TaxData, fromCreateButton: boolean = false): Promise<void> {
        const data = response.data[0];
        if (!data) return;
        if (props?.onLookupSelection && data["error_message"]) {
            await CommonDialogs.showMessage(data["error_message"]);
        }
        const taxCharge = data["tax_row"];
        let modelRow: ModelRow;

        if (fromCreateButton) {
            // Create button path
            const modelPath = props.rowType === "ORDER" ? "lme/ar/excisetax-charge" : "lme/ar/excisetax-charge-bill";
            modelRow = new ModelRow(modelPath);
            this.mapTaxChargeToModelRow(taxCharge, modelRow);
            (props.table as Table).addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: true });
        } else {
            // Textbox path
            const textbox = props.taxTextbox as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            modelRow = textbox.boundRow;
            this.mapTaxChargeToModelRow(taxCharge, modelRow);
            tableRow.displayComponentValues();
        }
    }

    private mapTaxChargeToModelRow(taxCharge: any, modelRow: ModelRow): void {
        const {
            descr,
            method,
            units,
            rate,
            customer_id,
            state_id,
            amount: amountProps,
            excisetax_id,
            tax_date,
            is_estimated
        } = taxCharge;

        modelRow.set("descr", descr);
        modelRow.set("method", method);
        modelRow.set("units", units);
        modelRow.set("rate", rate);
        modelRow.set("customer_id", customer_id);
        modelRow.set("state_id", state_id);
        modelRow.set("tax_date", tax_date);
        modelRow.set("excisetax_id", excisetax_id);
        modelRow.set("is_estimated", is_estimated ?? false);
        modelRow.setLookupModelData("state_id", { id: state_id });
        modelRow.setLookupModelData("excisetax_id", { id: excisetax_id, descr: descr });

        this.setModelAmount(modelRow, amountProps, customer_id);
    }

        private setModelAmount(modelRow: ModelRow, amountProps: any, customerId?: string): void {
        if (amountProps && !customerId) {
            // When the excise tax record has no customer_id of its own, default to the order's customer currency
            const orderCustomerCurrency = this.ordersDatasource.activeRow?.get("freight_charge_c",
                CurrencySettings.getSingleton().getTransactionalCurrency());
            amountProps["currency_code"] = orderCustomerCurrency;
        }
        modelRow.updateCurrency("amount", amountProps);
    }

    set currencyType(currencyType: string) {
        this._currencyType = currencyType;
    }

    get currencyType(): string {
        return this._currencyType;
    }

    getOrderType(): string {
        if (this.ratesLayout instanceof BillingRates) {
            return "BILLING"
        } else if (this.ratesLayout instanceof EdiBillingRates) {
            return "EDI_BILLING"
        } else if (this.ratesLayout instanceof RecOrderRates) {
            return "REC_ORDER"
        } else if (this.ratesLayout instanceof OrderRates) {
            return "ORDER"
        } else {
            return "QUOTE"
        }
    }

    addCurrencyToProps(props, row: ModelRow, fieldName: string, propName: string) {
        const currencyValue = row.get(fieldName);
        this.addCurrencyValueToProps(props, propName, currencyValue);
    }

    addCurrencyValueToProps(props: any, propName: string, currencyValue: any) {
        props[propName] = currencyValue;
    }

    async taxOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox: Textbox = event.target as Textbox;
            await this.calculateTaxCharges({
                taxRow: textbox.boundRow,
                taxTextbox: textbox
            }, false);
        }
    }

    async taxOnSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const textbox: Textbox = event.target as Textbox;
            await this.calculateTaxCharges({
                taxRow: textbox.boundRow,
                taxTextbox: textbox
            }, false);
        }
    }

    async taxOnDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            const textbox: Textbox = event.target as Textbox;
            await this.calculateTaxCharges({
                taxRow: textbox.boundRow,
                taxTextbox: textbox
            }, false);
        }
    }

    tableTaxOnContentsChanged() {
        this.ordersDatasource.activeRow.set("excisetax_total", this.getTaxChargeBillTotal(false));
        this.ordersDatasource.activeRow.set("totalcharge_and_excisetax", this.getTaxChargeBillTotal(true));
    }

    getTaxChargeBillTotal(includeTotalCharge: boolean) {
        const activeRow = this.ordersDatasource.activeRow;
        let totalChargeField: string;
        if (this.ratesLayout instanceof BillingRates) {
            totalChargeField = "total_charges";
        } else {
            totalChargeField = "total_charge";
        }
        const totalCharge = includeTotalCharge ? activeRow.get(totalChargeField) : null;
        const conversionDate: Date = activeRow.getCurrency(totalChargeField, new Currency(0.0)).getConversionDate();
        const currencyCode = activeRow.get("freight_charge_c", CurrencySettings.getSingleton().getTransactionalCurrency());
        return TaxUtil.calculateTaxChargeTotal(this.ratesLayout.getTaxDatasource(),
            new Currency({
                amount: totalCharge?.amount ?? 0,
                currency_code: currencyCode,
                conversion_date: conversionDate
            }));
    }

    public static calculateTaxChargeTotal(taxDataSource: DataSource, totalCharge?: Currency) {
        let totalAmount: number = totalCharge?.amount ?? 0;
        const currencyCode = totalCharge?.currency_code ?? CurrencySettings.getSingleton().getTransactionalCurrency();
        const conversionDate = totalCharge?.conversion_date ?? new Date();

        const totalOtherCharges = new Currency({
            amount: totalAmount,
            currency_code: currencyCode,
            conversion_date: conversionDate
        });
        for (const row of taxDataSource.data) {
            const currency = row.getNumber("amount_n", 0);
            if (currency) {
                totalAmount += currency;
            }
        }
        totalOtherCharges.setAmount(totalAmount);
        return totalOtherCharges;
    }

    /**
     * Updates excise taxes for an order or billing record by fetching calculated tax data from the server.
     * This method can be used by both OrderRates and BillingRates.
     *
     * @param taxesList - Optional list of tax rows to use instead of fetching from server
     * @param showDialog - Whether to show error dialogs to the user
     * @param rowType - The type of row: "O" for Order or "B" for Billing
     */
    public async updateExciseTaxes(taxesList?: ModelRow[], showDialog: boolean = false, rowType: "O" | "B" = "O"): Promise<void> {
        if (this.ordersDatasource.mode === DataSourceMode.SEARCH || (!this.ordersDatasource.activeRow.getBoolean("excise_taxable", false) && rowType === "O")) return;

        const stopRows: ModelRow[] = this.ordersDatasource.childDataSources.find((source) => source.id == "sourceStop")?.data;
        if (stopRows?.some(stop => stop.isNull("sched_arrive_early"))) {
            if (showDialog) {
                await CommonDialogs.showError("Stops require pickup/delivery dates in order to create taxes.");
            }
            return;
        }

        let response: any;
        if (!taxesList) {
            const orderRow = this.ordersDatasource.getDataboundValues(null, true);
            orderRow.set("id", this.ordersDatasource.activeRow.get("id"));

            // Set the appropriate charge field based on row type
            if (rowType === "B") {
                orderRow.set("linehaul_chg", this.ordersDatasource.activeRow.getCurrency("linehaul_chg"));
            } else {
                orderRow.set("freight_charge", this.ordersDatasource.activeRow.getCurrency("freight_charge"));
            }

            response = await Api.post("lme/ar/create-tax", {
                order_row: orderRow,
                order_id: this.ordersDatasource.activeRow.get("order_id"),
                billing_id: this.ordersDatasource.activeRow.get("id"),
                row_type: rowType
            });
        }

        const taxes = taxesList || response?.data?.[0]?.taxes;
        const taxTable = this.ratesLayout.getTaxTable();
        const taxDatasource = this.ratesLayout.getTaxDatasource();

        // Remove all existing estimated taxes before adding new ones
        for (let i = taxTable.rows.length - 1; i >= 0; i--) {
            const row = taxTable.rows[i];
            taxDatasource._deleteTableRow(row.data);
        }
        if (taxes?.length > 0) {
            for (const tax of taxes) {
                const modelRow = new ModelRow(taxDatasource.url);
                modelRow.setValues(tax);
                modelRow.set("amount", modelRow.getCurrency("amount"));
                taxTable.addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: true });
            }
        }
    }
}
